import { useQuery } from "@tanstack/react-query";
import { Table, Th, Td, fmtUsd, fmtPct } from "@/components/data-table";
import { ChainBadge } from "@/components/chain-badge";
import { PackHoverPreview } from "@/components/pack-hover-preview";
import { ScrambleText } from "@/components/scramble-text";
import { makeWatchlistKey, useWatchlist } from "@/hooks/use-watchlist";

interface PackOddsStat {
  pack: { chain: "base" | "apechain"; address: string; name: string; priceUsd: number };
  odds: Array<{ range: string; percent: string; low: number; high: number; prob: number }>;
  totalProbability: number;
  evPerPullUsd: number;
  netEvUsd: number;
  edgePct: number;
}

// Bucket border colors matching slab.cash: the lowest-value bucket is green,
// stepping up through cyan / blue / purple / gold as the value tier rises.
// Inline-styled so we don't force every shade into the Tailwind palette.
const ODDS_TIER_COLORS = [
  "#34d399", // emerald — common / widest bucket
  "#22d3ee", // cyan
  "#60a5fa", // blue
  "#c084fc", // purple
  "#fbbf24", // gold — rarest high-value bucket
];

function oddsTierStyle(idx: number): React.CSSProperties {
  const c = ODDS_TIER_COLORS[Math.min(idx, ODDS_TIER_COLORS.length - 1)];
  return {
    // hex alpha: 99 ≈ 60%, 14 ≈ 8%
    borderColor: `${c}99`,
    backgroundColor: `${c}14`,
    color: c,
  };
}

export default function EVPacksPage() {
  const { add, remove, isWatched } = useWatchlist();
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["packs"],
    queryFn: async () => {
      const res = await fetch(`/api/packs`);
      return res.json() as Promise<{ stats: PackOddsStat[] }>;
    },
  });

  function packKey(p: PackOddsStat["pack"]): string {
    return makeWatchlistKey({
      kind: "PACK",
      chain: p.chain,
      address: p.address,
      tokenId: "",
    });
  }

  function toggleWatch(s: PackOddsStat) {
    const key = packKey(s.pack);
    if (isWatched(key)) {
      remove(key);
      return;
    }
    add({
      kind: "PACK",
      chain: s.pack.chain,
      chainId: s.pack.chain === "base" ? 8453 : 33139,
      address: s.pack.address,
      tokenId: "",
      description: s.pack.name,
      valueUsd: s.pack.priceUsd,
    });
  }

  return (
    <div className="p-3 md:p-4 space-y-3 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div className="space-y-1 min-w-0">
          <h1 className="text-lg md:text-xl font-bold uppercase tracking-[0.2em] neon-text cursor">
            <ScrambleText text="ev-ranked packs" duration={900} revealEach={45} />
          </h1>
          <p className="text-[11px] text-muted max-w-3xl font-mono leading-relaxed">
            <span className="text-accent2">›</span> EV per pull = Σ&nbsp;P(bucket)&nbsp;×&nbsp;midpoint(bucket)
            &nbsp;·&nbsp; sourced from <span className="text-accent">api.slab.cash/v1/machines</span>
            <br />
            <span className="text-accent2">›</span> sorted by edge (net EV / pack price) — edge &gt; 0 means positive expectation
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="self-start sm:self-auto shrink-0 rounded border border-border bg-panel/60 px-3 py-1 text-xs font-mono uppercase tracking-wider hover:border-accent hover:text-accent hover:shadow-glow transition-all disabled:opacity-50"
        >
          {isFetching ? "› syncing…" : "[refresh]"}
        </button>
      </div>

      {isLoading ? (
        <Loading />
      ) : (
        <>
        {/* Mobile: stacked cards, one per pack. */}
        <div className="md:hidden space-y-2">
          {(data?.stats ?? []).map((s, i) => {
            const watched = isWatched(packKey(s.pack));
            const chainId = s.pack.chain === "base" ? 8453 : 33139;
            return (
              <div
                key={s.pack.chain + s.pack.address}
                className="rounded-md border border-border bg-panel/70 p-3 brackets animate-fadeIn"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] text-muted font-mono mb-0.5">
                      #{String(i + 1).padStart(2, "0")}
                    </div>
                    <PackHoverPreview chainId={chainId} machineAddress={s.pack.address}>
                      <div className="cursor-pointer">
                        <div className="font-medium text-text hover:text-accent transition-colors truncate">
                          {s.pack.name}
                        </div>
                        <div className="text-[10px] text-muted font-mono">
                          {short(s.pack.address)}
                        </div>
                      </div>
                    </PackHoverPreview>
                  </div>
                  <ChainBadge chain={s.pack.chain} />
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] font-mono">
                  <div className="flex justify-between">
                    <span className="text-muted">Price</span>
                    <span className="tabular-nums">{fmtUsd(s.pack.priceUsd)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted">EV/pull</span>
                    <span className="tabular-nums text-accent2">{fmtUsd(s.evPerPullUsd)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted">Net EV</span>
                    <span
                      className={
                        "tabular-nums font-bold " +
                        (s.netEvUsd > 0 ? "text-success" : s.netEvUsd < 0 ? "text-danger" : "")
                      }
                    >
                      {fmtUsd(s.netEvUsd, { sign: true })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted">Edge</span>
                    <span
                      className={
                        "tabular-nums font-bold " +
                        (s.edgePct > 0 ? "text-success" : "text-danger")
                      }
                    >
                      {fmtPct(s.edgePct, { sign: true })}
                    </span>
                  </div>
                </div>
                <div className="mt-2 pt-2 border-t border-border/60 flex items-center justify-between gap-2">
                  <div className="flex flex-wrap gap-1 min-w-0">
                    {s.odds.slice(0, 3).map((o, idx) => (
                      <span
                        key={o.range}
                        style={oddsTierStyle(idx)}
                        className="rounded border px-1 py-0.5 text-[9px] font-mono"
                        title={`midpoint $${((o.low + o.high) / 2).toFixed(0)}`}
                      >
                        <span className="font-semibold">{o.range}</span>{" "}
                        <span className="opacity-70">{o.percent}</span>
                      </span>
                    ))}
                  </div>
                  <button
                    onClick={() => toggleWatch(s)}
                    className={
                      "shrink-0 rounded border px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider transition-all " +
                      (watched
                        ? "border-gold/60 text-gold bg-gold/10 shadow-[0_0_8px_rgba(255,176,32,0.4)]"
                        : "border-border text-muted")
                    }
                  >
                    {watched ? "★" : "☆"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <Table>
          <thead>
            <tr>
              <Th>#</Th>
              <Th>Pack</Th>
              <Th>Chain</Th>
              <Th className="text-right">Price</Th>
              <Th className="text-right">EV / pull</Th>
              <Th className="text-right">Net EV</Th>
              <Th className="text-right">Edge</Th>
              <Th>Published odds</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {(data?.stats ?? []).map((s, i) => (
              <tr
                key={s.pack.chain + s.pack.address}
                className="row-glow align-top transition-colors"
              >
                <Td className="text-muted">{String(i + 1).padStart(2, "0")}</Td>
                <Td>
                  <PackHoverPreview
                    chainId={s.pack.chain === "base" ? 8453 : 33139}
                    machineAddress={s.pack.address}
                  >
                    <div className="cursor-pointer">
                      <div className="font-medium text-text hover:text-accent transition-colors">
                        {s.pack.name}
                      </div>
                      <div className="text-[10px] text-muted font-mono">{short(s.pack.address)}</div>
                    </div>
                  </PackHoverPreview>
                </Td>
                <Td>
                  <ChainBadge chain={s.pack.chain} />
                </Td>
                <Td className="text-right">{fmtUsd(s.pack.priceUsd)}</Td>
                <Td className="text-right text-accent2">{fmtUsd(s.evPerPullUsd)}</Td>
                <Td
                  className={
                    "text-right font-bold " +
                    (s.netEvUsd > 0 ? "text-success" : s.netEvUsd < 0 ? "text-danger" : "")
                  }
                >
                  {fmtUsd(s.netEvUsd, { sign: true })}
                </Td>
                <Td
                  className={
                    "text-right font-bold " +
                    (s.edgePct > 0 ? "text-success" : "text-danger")
                  }
                >
                  {fmtPct(s.edgePct, { sign: true })}
                </Td>
                <Td>
                  <div className="flex flex-wrap gap-1">
                    {s.odds.map((o, idx) => (
                      <span
                        key={o.range}
                        style={oddsTierStyle(idx)}
                        className="rounded border px-1.5 py-0.5 text-[10px] font-mono transition-all"
                        title={`midpoint $${((o.low + o.high) / 2).toFixed(0)}`}
                      >
                        <span className="font-semibold">{o.range}</span>{" "}
                        <span className="opacity-70">{o.percent}</span>
                      </span>
                    ))}
                  </div>
                </Td>
                <Td>
                  {(() => {
                    const watched = isWatched(packKey(s.pack));
                    return (
                      <button
                        onClick={() => toggleWatch(s)}
                        className={
                          "rounded border px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider transition-all " +
                          (watched
                            ? "border-gold/60 text-gold bg-gold/10 hover:bg-gold/20 shadow-[0_0_8px_rgba(255,176,32,0.4)]"
                            : "border-border text-muted hover:border-accent hover:text-accent hover:shadow-glow")
                        }
                        title={watched ? "Remove from watchlist" : "Add to watchlist"}
                      >
                        {watched ? "[watching ★]" : "[watch ☆]"}
                      </button>
                    );
                  })()}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
        </>
      )}
    </div>
  );
}

function Loading() {
  return (
    <div className="rounded border border-border bg-panel/60 p-6 text-sm text-muted font-mono">
      <span className="text-accent2">›</span> connecting to api.slab.cash
      <span className="animate-blink text-accent ml-1">_</span>
    </div>
  );
}

function short(a: string) {
  return a.slice(0, 6) + "…" + a.slice(-4);
}
